import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  convertEvaluationType,
  convertStatus,
  convertStatusUrl,
} from '../../../../api/enumerations';
import {
  BankDossier,
  BankRelatedRequest,
} from '../../../../api/workOrders/bankDossier';
import { WorkOrderData } from '../../../../api/workOrders/types';
import { Constants } from '../../../../constants/bankDossier';
import { IconOpenInNewMS } from '../../../../constants/icons';
import { dossierDateTime, textOrEmpty } from '../../../../helpers/bankDossier';
import { StatusPill } from '../../../UI/StatusPill';
import { DossierColumn, DossierTable } from '../../DossierTable';
import { BodyCell, CellSecondary, MutedCell } from '../../DossierTable/styles';
import { RelatedDescription } from '../styles';

interface SectionProps {
  dossier: BankDossier;
  workOrder: WorkOrderData;
}

const { columns } = Constants.related;

const relatedColumns: DossierColumn[] = [
  { key: 'os', label: columns.os },
  { key: 'control', label: columns.control },
  { key: 'ordinal', label: columns.ordinal },
  { key: 'company', label: columns.company },
  { key: 'report_type', label: columns.reportType },
  { key: 'status', label: columns.status },
  { key: 'created_at', label: columns.createdAt },
  { key: 'actions', label: '', width: '120px' },
];

export function RelatedRequestsSection({
  dossier,
  workOrder,
}: SectionProps): JSX.Element {
  const navigate = useNavigate();

  const current: BankRelatedRequest = {
    work_order_id: workOrder.id,
    reference_number: workOrder.reference_number,
    bank_control_number: dossier.identification.bank_control_number,
    request_ordinal: dossier.identification.request_ordinal ?? 1,
    appraisal_company: workOrder.worker_company?.name ?? null,
    report_type: convertEvaluationType(workOrder.evaluation_type),
    status: workOrder.status,
    status_label: null,
    created_at: workOrder.created_at,
  };

  const rows = [...dossier.related_requests, current].sort(
    (a, b) => a.request_ordinal - b.request_ordinal
  );

  const openRow = (row: BankRelatedRequest): void => {
    if (row.work_order_id === null || row.status === null) return;
    navigate(
      `/home/property/${row.work_order_id}/${convertStatusUrl(row.status)}`
    );
  };

  const renderRow = (row: BankRelatedRequest): JSX.Element => {
    const isCurrent = row.work_order_id === workOrder.id;
    return (
      <>
        <BodyCell>
          {row.reference_number ?? Constants.related.notMigrated}
          {isCurrent && (
            <CellSecondary>{Constants.related.current}</CellSecondary>
          )}
        </BodyCell>
        <MutedCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {textOrEmpty(row.bank_control_number)}
        </MutedCell>
        <BodyCell>{Constants.related.ordinal(row.request_ordinal)}</BodyCell>
        <BodyCell>{textOrEmpty(row.appraisal_company)}</BodyCell>
        <MutedCell>{textOrEmpty(row.report_type)}</MutedCell>
        <BodyCell>
          <StatusPill tone="neutral">
            {row.status !== null
              ? convertStatus(row.status)
              : textOrEmpty(row.status_label)}
          </StatusPill>
        </BodyCell>
        <BodyCell sx={{ whiteSpace: 'nowrap' }}>
          {dossierDateTime(row.created_at)}
        </BodyCell>
        <BodyCell align="right">
          {!isCurrent && row.work_order_id !== null && (
            <Button variant="text" size="small" onClick={() => openRow(row)}>
              {IconOpenInNewMS}
              {Constants.related.open}
            </Button>
          )}
        </BodyCell>
      </>
    );
  };

  return (
    <>
      <RelatedDescription>
        {dossier.related_requests.length === 0
          ? Constants.related.empty
          : Constants.related.description}
      </RelatedDescription>
      <DossierTable<BankRelatedRequest>
        columns={relatedColumns}
        rows={rows}
        getRowId={(row) => `${row.request_ordinal}-${row.bank_control_number}`}
        renderRow={renderRow}
        rowClassName={(row) =>
          row.work_order_id === workOrder.id ? 'is-current' : undefined
        }
        emptyMessage={Constants.related.empty}
        ariaLabel={Constants.sections.related}
      />
    </>
  );
}
